import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, LogIn, LogOut, Coffee, Calendar, TrendingUp, Users, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useIsMounted } from '@/hooks/useSafeState';

const Timesheet = () => {
  const { user, token } = useAuth();
  const isMountedRef = useIsMounted();
  const [myEntries, setMyEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [todayEntry, setTodayEntry] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [summary, setSummary] = useState([]);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateData, setUpdateData] = useState({
    break_start: '',
    break_end: '',
    clock_out: '',
    notes: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    const controller = new AbortController();
    
    fetchMyEntries(controller.signal);
    if (user?.role === 'admin') {
      fetchAllEntries(controller.signal);
      fetchEmployees(controller.signal);
      fetchSummary(controller.signal);
    }

    return () => {
      controller.abort();
    };
  }, [selectedMonth, selectedEmployee]);

  const fetchMyEntries = async (signal) => {
    try {
      const response = await axios.get(`${API}/timesheet/my-entries`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: `${selectedMonth}-01`,
          end_date: `${selectedMonth}-31`
        },
        signal
      });
      
      if (!isMountedRef.current) return;
      
      setMyEntries(response.data);
      
      // Check if already clocked in today
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayEntry = response.data.find(e => e.date === today);
      setTodayEntry(todayEntry || null);
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return; // Request was cancelled, component unmounted
      }
      console.error('Error fetching my entries:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchAllEntries = async (signal) => {
    try {
      const params = {
        start_date: `${selectedMonth}-01`,
        end_date: `${selectedMonth}-31`
      };
      
      if (selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }
      
      const response = await axios.get(`${API}/timesheet/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        signal
      });
      
      if (!isMountedRef.current) return;
      setAllEntries(response.data);
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching all entries:', error);
    }
  };

  const fetchEmployees = async (signal) => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      
      if (!isMountedRef.current) return;
      const employeesList = response.data.filter(u => u.role === 'employee');
      setEmployees(employeesList);
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching employees:', error);
    }
  };

  const fetchSummary = async (signal) => {
    try {
      const params = { month: selectedMonth };
      if (selectedEmployee !== 'all') {
        params.employee_id = selectedEmployee;
      }
      
      const response = await axios.get(`${API}/timesheet/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        signal
      });
      
      if (!isMountedRef.current) return;
      setSummary(response.data);
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error('Error fetching summary:', error);
    }
  };

  const handleClockIn = async () => {
    try {
      const now = new Date();
      await axios.post(
        `${API}/timesheet/clock-in`,
        {
          clock_in: format(now, 'HH:mm'),
          notes: ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Pointage d\'entrée enregistré');
      fetchMyEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du pointage');
    }
  };

  const handleUpdate = async () => {
    if (!todayEntry) return;
    
    try {
      await axios.put(
        `${API}/timesheet/${todayEntry.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Pointage mis à jour');
      setUpdateDialogOpen(false);
      fetchMyEntries();
      if (user?.role === 'admin') {
        fetchAllEntries();
        fetchSummary();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Supprimer cette entrée de pointage ?')) return;
    
    try {
      await axios.delete(`${API}/timesheet/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entrée supprimée');
      fetchAllEntries();
      fetchSummary();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openUpdateDialog = () => {
    if (todayEntry) {
      setUpdateData({
        break_start: todayEntry.break_start || '',
        break_end: todayEntry.break_end || '',
        clock_out: todayEntry.clock_out || '',
        notes: todayEntry.notes || ''
      });
    }
    setUpdateDialogOpen(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
            Système de Pointage
          </h1>
          <p className="text-lg text-gray-600">
            {user?.role === 'admin' 
              ? 'Suivez les heures de travail de tous les employés' 
              : 'Gérez vos heures de travail quotidiennes'}
          </p>
        </div>

        {/* Today's Clock In/Out - Employee View */}
        {user?.role === 'employee' && (
          <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-50 to-indigo-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600">
                    <Clock className="h-10 w-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {todayEntry ? 'Pointage du jour' : 'Pas encore pointé'}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {format(new Date(), 'EEEE dd MMMM yyyy', { locale: fr })}
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  {!todayEntry ? (
                    <Button
                      size="lg"
                      onClick={handleClockIn}
                      className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
                    >
                      <LogIn className="mr-2 h-5 w-5" />
                      Pointer l'entrée
                    </Button>
                  ) : (
                    <>
                      {todayEntry.status === 'open' && (
                        <Button
                          size="lg"
                          onClick={openUpdateDialog}
                          className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
                        >
                          <Coffee className="mr-2 h-5 w-5" />
                          Gérer le pointage
                        </Button>
                      )}
                      {todayEntry.status === 'completed' && (
                        <Badge className="text-lg px-6 py-3 bg-green-600">
                          <LogOut className="mr-2 h-5 w-5" />
                          Journée complétée: {todayEntry.total_hours}h
                        </Badge>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {/* Today's details */}
              {todayEntry && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Entrée</p>
                    <p className="text-xl font-bold text-green-600">{todayEntry.clock_in || '--:--'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Pause début</p>
                    <p className="text-xl font-bold text-orange-600">{todayEntry.break_start || '--:--'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Pause fin</p>
                    <p className="text-xl font-bold text-orange-600">{todayEntry.break_end || '--:--'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-1">Sortie</p>
                    <p className="text-xl font-bold text-red-600">{todayEntry.clock_out || '--:--'}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Admin Statistics */}
        {user?.role === 'admin' && summary.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {summary.map(emp => (
              <Card key={emp.employee_id} className="border-0 shadow-xl">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-gray-900">{emp.employee_name}</h3>
                      <p className="text-sm text-gray-600">{selectedMonth}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-blue-100">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total heures:</span>
                      <span className="font-bold text-blue-600">{emp.total_hours.toFixed(2)}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Jours travaillés:</span>
                      <span className="font-bold text-gray-900">{emp.days_worked}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters - Admin Only */}
        {user?.role === 'admin' && (
          <Card className="border-0 shadow-xl">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Label>Mois</Label>
                  <Input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="mt-2"
                  />
                </div>
                <div className="flex-1">
                  <Label>Employé</Label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les employés</SelectItem>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Entries Table */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-6 w-6 text-red-600" />
              {user?.role === 'admin' ? 'Historique complet' : 'Mon historique'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {user?.role === 'admin' && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Employé</th>}
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Entrée</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Pause</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sortie</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
                    {user?.role === 'admin' && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(user?.role === 'admin' ? allEntries : myEntries).map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      {user?.role === 'admin' && (
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {entry.employee_name}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {format(new Date(entry.date), 'dd/MM/yyyy')}
                      </td>
                      <td className="px-4 py-3 text-sm text-green-600 font-semibold">
                        {entry.clock_in || '--:--'}
                      </td>
                      <td className="px-4 py-3 text-sm text-orange-600">
                        {entry.break_start && entry.break_end 
                          ? `${entry.break_start} - ${entry.break_end}` 
                          : '--:--'}
                      </td>
                      <td className="px-4 py-3 text-sm text-red-600 font-semibold">
                        {entry.clock_out || '--:--'}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-blue-600">
                        {entry.total_hours ? `${entry.total_hours}h` : '--'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={entry.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                          {entry.status === 'completed' ? 'Complété' : 'En cours'}
                        </Badge>
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(entry.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {(user?.role === 'admin' ? allEntries : myEntries).length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune entrée de pointage pour cette période</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Update Dialog */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gérer le pointage du jour</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pause début (HH:MM)</Label>
                <Input
                  type="time"
                  value={updateData.break_start}
                  onChange={(e) => setUpdateData({...updateData, break_start: e.target.value})}
                />
              </div>
              <div>
                <Label>Pause fin (HH:MM)</Label>
                <Input
                  type="time"
                  value={updateData.break_end}
                  onChange={(e) => setUpdateData({...updateData, break_end: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Heure de sortie (HH:MM)</Label>
              <Input
                type="time"
                value={updateData.clock_out}
                onChange={(e) => setUpdateData({...updateData, clock_out: e.target.value})}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={updateData.notes}
                onChange={(e) => setUpdateData({...updateData, notes: e.target.value})}
                placeholder="Notes optionnelles..."
                rows={3}
              />
            </div>
            <Button
              onClick={handleUpdate}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
            >
              Enregistrer les modifications
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Timesheet;
